import { createSkyCathedralGuidanceMaterial } from './242-sky-cathedral-framing.mjs';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import { skyCathedralPose } from '../../../packages/domain/src/100-sky-cathedral-pose.mjs';
import {
  createSkyCathedralCollision,
  skyCathedralArtFrame,
} from '../../../packages/domain/src/114-sky-cathedral-art-geometry.mjs';

const hashes = new Set([
  '038a6e8b8849687aab8d3b5db8a192ab92e1f88f8c62bef73532fba14c192832',
  '0933bfc01055a0ea4193a8e009719a628c720ffcdebf41c1ff518d0e034bccda',
]);
const matrixFor = (T, frame) => {
  const { origin: o, right: r, up: u, back: b } = frame;
  return new T.Matrix4().set(
    r.x,
    u.x,
    b.x,
    o.x,
    r.y,
    u.y,
    b.y,
    o.y,
    r.z,
    u.z,
    b.z,
    o.z,
    0,
    0,
    0,
    1,
  );
};

/** Pin the actual reviewed full/mobile source bytes before parsing. */
export async function verifySkyCathedralAsset(bytes) {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const hash = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
  if (!hashes.has(hash))
    throw new Error('Cathedral artwork does not match its physical source contract.');
}

/** The reviewed GLB is a rigid skin: every triangle's vertices have one identical
 * unit-weight bone. Extract all visible textures' underlying triangles into100's
 * driver frames, preserving the hollow nave and textured outer contact shape.
 * Blended/morphed/unknown geometry fails instead of silently using proxies. */
export function extractSkyCathedralCollision(T, scene) {
  const neutral = skyCathedralPose(),
    parts = new Map();
  scene.updateMatrixWorld(true);
  scene.traverse((mesh) => {
    if (!mesh.isMesh) return;
    if (!mesh.isSkinnedMesh || mesh.geometry.morphAttributes.position?.length)
      throw new Error('Cathedral requires the reviewed rigid skinned assembly.');
    mesh.skeleton.update();
    const geometry = mesh.geometry,
      positions = geometry.attributes.position,
      indices = geometry.index,
      joints = geometry.attributes.skinIndex,
      weights = geometry.attributes.skinWeight;
    if (!positions || !joints || !weights) throw new Error('Cathedral skin attributes missing.');
    const vertices = Array.from({ length: positions.count }, (_, i) => {
      if (
        Math.abs(weights.getX(i) - 1) > 1e-6 ||
        Math.abs(weights.getY(i)) + Math.abs(weights.getZ(i)) + Math.abs(weights.getW(i)) > 1e-6
      )
        throw new Error('Cathedral contact geometry must have exactly one rigid weight.');
      const bone = mesh.skeleton.bones[joints.getX(i)];
      const driver = bone && skyCathedralArtFrame(neutral, bone.name);
      if (!driver) throw new Error('Cathedral surface has an unknown canonical driver.');
      const point = mesh
        .getVertexPosition(i, new T.Vector3())
        .applyMatrix4(mesh.matrixWorld)
        .applyMatrix4(matrixFor(T, driver).invert());
      return { bone: bone.name, point: { x: point.x, y: point.y, z: point.z } };
    });
    const count = indices?.count ?? positions.count;
    if (count % 3) throw new Error('Cathedral has incomplete contact triangles.');
    for (let i = 0; i < count; i += 3) {
      const tri = [0, 1, 2].map(
        (offset) => vertices[indices ? indices.getX(i + offset) : i + offset],
      );
      if (tri.some((v) => v.bone !== tri[0].bone))
        throw new Error('Cathedral triangle crosses rigid drivers.');
      const list = parts.get(tri[0].bone) ?? [];
      list.push({ a: tri[0].point, b: tri[1].point, c: tri[2].point });
      parts.set(tri[0].bone, list);
    }
  });
  const handle = createSkyCathedralCollision(
    [...parts].map(([bone, triangles]) => ({ bone, triangles })),
  );
  if (!handle) throw new Error('Cathedral exact geometry failed validation.');
  return handle;
}

/** Essential hazard presentation consumes the exact live contact endpoints.
 * Charging remains visible with reduced motion; no clock or random flicker can
 * move a fence/strike away from its physical sweep. Private buffers are bounded. */
function createSignals(T) {
  const group = new T.Group();
  group.name = 'Cathedral physical lightning and fences';
  const resources = new Set();
  const own = (value) => {
    resources.add(value);
    return value;
  };
  const lineGeometry = own(new T.BufferGeometry());
  lineGeometry.setAttribute('position', new T.Float32BufferAttribute(new Float32Array(256 * 3), 3));
  lineGeometry.setAttribute('color', new T.Float32BufferAttribute(new Float32Array(256 * 3), 3));
  const lines = new T.LineSegments(
    lineGeometry,
    own(
      new T.LineBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
        toneMapped: false,
      }),
    ),
  );
  lines.frustumCulled = false;
  group.add(lines);
  const panels = Array.from({ length: 2 }, () => {
    const geometry = own(new T.BufferGeometry());
    geometry.setAttribute('position', new T.Float32BufferAttribute(new Float32Array(18), 3));
    const material = own(
      new T.MeshBasicMaterial({
        color: '#b184ff',
        transparent: true,
        opacity: 0.18,
        depthWrite: false,
        side: T.DoubleSide,
        toneMapped: false,
      }),
    );
    const mesh = new T.Mesh(geometry, material);
    mesh.frustumCulled = false;
    group.add(mesh);
    return mesh;
  });
  const strikeMaterial = own(
    new T.MeshBasicMaterial({
      color: '#ffd276',
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      toneMapped: false,
    }),
  );
  const strike = new T.Mesh(own(new T.CylinderGeometry(1, 1, 1, 8)), strikeMaterial);
  const target = new T.Mesh(own(new T.RingGeometry(1.17, 1.27, 32)), strikeMaterial);
  target.rotation.x = -Math.PI / 2;
  strikeMaterial.side = T.DoubleSide;
  group.add(strike, target);
  const axis = new T.Vector3(0, 1, 0),
    delta = new T.Vector3(),
    tint = new T.Color();
  function draw(state) {
    let count = 0;
    const positions = lineGeometry.attributes.position,
      colors = lineGeometry.attributes.color;
    const segment = (a, b, color) => {
      if (count + 2 > positions.count) return;
      tint.setHex(color);
      for (const p of [a, b]) {
        positions.setXYZ(count, p.x, p.y, p.z);
        colors.setXYZ(count++, tint.r, tint.g, tint.b);
      }
    };
    panels.forEach((mesh, index) => {
      const fence = state.fences?.[index];
      mesh.visible = Boolean(fence);
      if (!fence) return;
      const buffer = mesh.geometry.attributes.position;
      fence.triangles.forEach((tri, i) =>
        ['a', 'b', 'c'].forEach((key, j) => {
          const p = tri[key];
          buffer.setXYZ(i * 3 + j, p.x, p.y, p.z);
        }),
      );
      buffer.needsUpdate = true;
      mesh.material.opacity = fence.active ? 0.22 : 0.04 + fence.charge * 0.07;
      const c = fence.vertices,
        color = fence.active ? 0xdfc0ff : 0xa07dcc;
      c.forEach((point, i) => segment(point, c[(i + 1) % c.length], color));
      for (let row = 1; row < 8; row++) {
        const t = row / 8;
        const lerp = (a, b) => ({
          x: a.x + (b.x - a.x) * t,
          y: a.y + (b.y - a.y) * t,
          z: a.z + (b.z - a.z) * t,
        });
        segment(lerp(c[0], c[3]), lerp(c[1], c[2]), color);
      }
    });
    const live = state.cathedral?.strike;
    strike.visible = target.visible = Boolean(live);
    if (live) {
      delta.subVectors(live.target, live.origin);
      const length = delta.length(),
        active = live.stage === 'active';
      strike.position.copy(live.origin).add(live.target).multiplyScalar(0.5);
      strike.quaternion.setFromUnitVectors(axis, delta.normalize());
      strike.scale.set(active ? 0.11 : 0.035, length, active ? 0.11 : 0.035);
      strikeMaterial.color.setHex(
        active ? 0xf5eaff : live.stage === 'committed' ? 0xff8f42 : 0xffd276,
      );
      strikeMaterial.opacity = active ? 1 : live.stage === 'committed' ? 0.85 : 0.45;
      target.position.copy(live.target);
    }
    lineGeometry.setDrawRange(0, count);
    positions.needsUpdate = colors.needsUpdate = true;
  }
  return {
    group,
    panels,
    strike,
    target,
    draw,
    dispose() {
      for (const value of resources) value.dispose();
      resources.clear();
    },
  };
}

/** Presentation follows the exact canonical pose used by contact policy, never
 * an independent animation clip clock. Geometry and textures stay shared; each
 * instance owns its skeletons, transforms and private guidance materials. */
export function createSkyCathedralArt(T, gltf) {
  const instances = new Set(),
    neutral = skyCathedralPose();
  function create() {
    const group = new T.Group(),
      scene = cloneSkeleton(gltf.scene);
    group.name = 'Cathedral of Thunder';
    group.userData.skyCathedral = true;
    group.add(scene);
    const signals = createSignals(T);
    group.add(signals.group);
    scene.updateMatrixWorld(true);
    const bindings = [],
      guidance = [],
      skeletons = new Set();
    scene.traverse((node) => {
      if (node.isSkinnedMesh) {
        skeletons.add(node.skeleton);
        guidance.push(createSkyCathedralGuidanceMaterial(T, node));
        node.frustumCulled = false;
        node.castShadow = true;
        node.receiveShadow = true;
      }
      if (!node.isBone) return;
      const driver = skyCathedralArtFrame(neutral, node.name);
      if (!driver) throw new Error(`Unknown Cathedral articulation: ${node.name}`);
      bindings.push({
        bone: node,
        offset: matrixFor(T, driver).invert().multiply(node.matrixWorld),
      });
    });
    group.userData.cathedralArt = { scene, bindings, skeletons, signals, guidance };
    instances.add(group);
    return group;
  }
  function animate(group, item, reduced, state) {
    const pose = state.cathedralPose ?? state.cathedral?.pose;
    group.visible = Boolean(pose?.valid);
    if (!group.visible) return;
    group.userData.cathedralArt.signals.draw(state);
    for (const material of group.userData.cathedralArt.guidance)
      material.update(state.cathedralGuidance);
    // The assembly was authored in absolute canonical metres, including its4m
    // foundations. Camera-driven scaling or a second boss-root offset is invalid.
    group.position.set(0, 0, 0);
    group.rotation.set(0, 0, 0);
    group.scale.setScalar(1);
    group.updateMatrixWorld(true);
    for (const { bone, offset } of group.userData.cathedralArt.bindings) {
      const frame = skyCathedralArtFrame(pose, bone.name);
      if (!frame) throw new Error(`Cathedral pose lost driver: ${bone.name}`);
      const world = matrixFor(T, frame).multiply(offset);
      bone.matrix.copy(bone.parent.matrixWorld).invert().multiply(world);
      bone.matrixAutoUpdate = false;
      bone.updateMatrixWorld(true);
    }
    for (const skeleton of group.userData.cathedralArt.skeletons) skeleton.update();
  }
  function release(group) {
    if (!instances.delete(group)) return;
    group.userData.cathedralArt.signals.dispose();
    for (const material of group.userData.cathedralArt.guidance) material.dispose();
    for (const skeleton of group.userData.cathedralArt.skeletons) skeleton.dispose();
    group.removeFromParent();
  }
  return {
    create,
    animate,
    release,
    dispose() {
      for (const group of [...instances]) release(group);
    },
  };
}
